import React from "react";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import style from "../searchWorksheet.module.css";
import SearchWorksheetButtons from "../../../components/searchWorksheetButtons";
import { useAppSelector } from "../../../globalState/hooks";
import { formatNumberWithDigit } from "../../../utils/formatNumber";
import { useTranslation } from "react-i18next";
import { getScheduleCFinalObligation } from "../../../utils/getScheduleCCalculate";
import { getBasicFinalValueFor2Line } from "../../../utils/getBasicInfoCalculate";

const ScheduleCTab: React.FC = () => {
  const WorkSheetData = useAppSelector((state) => state.createWorksheet);
  const { t } = useTranslation();



  const noncustodialName = WorkSheetData.non_custodial_parent || "";
  const custodialName =
    noncustodialName === WorkSheetData.parent_name_1
      ? WorkSheetData.parent_name_2
      : WorkSheetData.parent_name_1;

  const A = WorkSheetData.parenting_time_schedule_c?.schedule_c_non_custodial_parenting_days || "";
  const B = WorkSheetData.parenting_time_schedule_c?.schedule_c_custodial_parenting_days || "";
  
  const hasParentingTimeData = A || B;
  const isBothParentsNCP = WorkSheetData.non_custodial_parent && WorkSheetData.non_custodial_parent.includes(" and ");
  
  // Temporarily set root level values for calculation function
  const tempWorkSheetData = {
    ...WorkSheetData,
    schedule_c_non_custodial_parenting_days: A,
    schedule_c_custodial_parenting_days: B
  };
  
  // Get BCSO shares from existing function
  const { FinalValue1, FinalValue2 } = getBasicFinalValueFor2Line(WorkSheetData);
  const AS = WorkSheetData.non_custodial_parent === WorkSheetData.parent_name_1 ? FinalValue2 : FinalValue1;
  const BS = WorkSheetData.non_custodial_parent === WorkSheetData.parent_name_1 ? FinalValue1 : FinalValue2;
  const finalObligation = hasParentingTimeData && !isBothParentsNCP ? getScheduleCFinalObligation(tempWorkSheetData) : 0;

  // Debug logging
  console.log('Schedule C Debug:', {
    A, B, AS, BS, finalObligation,
    nonCustodialParent: WorkSheetData.non_custodial_parent,
    FinalValue1, FinalValue2,
    // Check what getScheduleCFinalObligation expects
    root_parenting_days_A: WorkSheetData.schedule_c_non_custodial_parenting_days,
    nested_parenting_days_A: WorkSheetData.parenting_time_schedule_c?.schedule_c_non_custodial_parenting_days,
    root_parenting_days_B: WorkSheetData.schedule_c_custodial_parenting_days,
    nested_parenting_days_B: WorkSheetData.parenting_time_schedule_c?.schedule_c_custodial_parenting_days
  });

  // Line 6 values for display
  const line6Value1 = hasParentingTimeData && !isBothParentsNCP && WorkSheetData.non_custodial_parent === WorkSheetData.parent_name_1 
    ? (finalObligation !== 0 ? -finalObligation : 0)
    : 0;
  const line6Value2 = hasParentingTimeData && !isBothParentsNCP && WorkSheetData.non_custodial_parent === WorkSheetData.parent_name_2 
    ? (finalObligation !== 0 ? -finalObligation : 0)
    : 0;

  return (
    <>
      <Box sx={{ pt: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell className={style.headerStyle}>
                  {t(
                  "Schedule C - All amounts/data that display on Schedule C were entered using the Online Child Support Calculator and can only be changed by selecting the button “Open This Worksheet.”"
                  )}
                </TableCell>
                <TableCell className={style.headerStyle}>
                  {t("Custodial Parent")}
                </TableCell>
                <TableCell className={style.headerStyle}>
                  {t("Noncustodial Parent")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("Parent Name")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {custodialName}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {noncustodialName}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("Parenting Days")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {B}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {A}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("BCSO Share")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {AS ? "$" + formatNumberWithDigit(AS) : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {BS ? "$" + formatNumberWithDigit(BS) : ""}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("Final Adjustment")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line6Value1 !== 0 ? "$" + formatNumberWithDigit(line6Value1) : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line6Value2 !== 0 ? "$" + formatNumberWithDigit(line6Value2) : ""}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {hasParentingTimeData && !isBothParentsNCP && finalObligation !== 0 && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: "#f0fff4", borderRadius: 2 }}>
            <Typography variant="h6" color="#22543d" align="center">
              {`${t("Final NCP Adjustment")}: $${formatNumberWithDigit(finalObligation)} / ${t("month")}`}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <SearchWorksheetButtons />
      </Box>
    </>
  );
};

export default ScheduleCTab;